/* LT-2 recipe helpers \u2014 mirror of backend logic. */
import { v4 as uuidv4 } from "uuid";
const newId = () => uuidv4();

const f = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const r2 = (n) => Math.round(n * 100) / 100;
/* LT-38 — qty rule for the maths: a MISSING qty (null / "") is a rate-only
 * line and counts as 1; an explicit 0 is 0 (library lines now land at 0 so
 * an un-entered quantity can never price as one unit). */
export const lineQty = (v) => (v === null || v === undefined || v === "" ? 1 : f(v));

/* ============================================================
 * LT-20 — quantity inputs on a recipe material entry. Mirror of
 * backend `utils.resolve_entry_wastage` / `entry_cost_per_unit`.
 * Wastage tiers: line override → recipe entry → material default → 0%.
 * Blank means INHERIT, never zero. Wastage is applied to QUANTITY:
 *   net qty → wastage % → gross qty → rate → cost.
 * Coverage basis: `purchase_unit` (qty_per_unit is in sheets/bags…) or
 * `coverage_unit` (qty_per_unit is in m²… and is divided by the
 * snapshotted coverage_per_unit_applied). Missing coverage → null cost
 * (the entry FAILS visibly — never priced at zero, never silently
 * treated as purchase units).
 * ============================================================ */
export const WASTAGE_SOURCE_LABEL = {
  line: "overridden on this line",
  recipe: "set on this recipe",
  material: "inherited from material",
  default_zero: "no wastage set — 0%",
};

const isBlank = (v) => v === null || v === undefined || v === "";

export function resolveEntryWastage(entry, lineOverride = null) {
  if (!isBlank(lineOverride)) return { pct: f(lineOverride), source: "line" };
  if (!isBlank(entry.wastage_pct)) return { pct: f(entry.wastage_pct), source: "recipe" };
  if (!isBlank(entry.material_wastage_pct_applied)) {
    return { pct: f(entry.material_wastage_pct_applied), source: "material" };
  }
  return { pct: 0, source: "default_zero" };
}

/** Net purchase-unit quantity per ONE unit of the recipe. null = coverage missing. */
export function entryPurchaseQtyPerUnit(entry) {
  const qpu = f(entry.qty_per_unit);
  if ((entry.coverage_basis || "purchase_unit") === "coverage_unit") {
    const cov = Number(entry.coverage_per_unit_applied);
    if (!Number.isFinite(cov) || cov <= 0) return null;
    return qpu / cov;
  }
  return qpu;
}

export function entryHasCoverageError(entry) {
  return entryPurchaseQtyPerUnit(entry) == null;
}

/** Per-unit cost of a material entry incl. wastage. null when coverage is missing. */
export function entryCostPerUnit(entry, lineOverride = null) {
  const net = entryPurchaseQtyPerUnit(entry);
  if (net == null) return null;
  const { pct } = resolveEntryWastage(entry, lineOverride);
  return net * (1 + pct / 100) * f(entry.rate);
}

/** Sum of entry costs; failing entries contribute nothing (they are flagged, not zeroed silently). */
export function materialsRatePerUnit(entries, lineOverride = null) {
  return (entries || []).reduce((s, e) => s + (entryCostPerUnit(e, lineOverride) ?? 0), 0);
}

/** LT-20 fields copied verbatim from a recipe entry (library → snapshot). */
export function pickQuantityFields(e) {
  return {
    wastage_pct: isBlank(e.wastage_pct) ? null : f(e.wastage_pct),
    coverage_basis: e.coverage_basis === "coverage_unit" ? "coverage_unit" : "purchase_unit",
    wastage_pct_applied: e.wastage_pct_applied ?? null,
    wastage_source: e.wastage_source ?? null,
    material_wastage_pct_applied: e.material_wastage_pct_applied ?? null,
    coverage_per_unit_applied: e.coverage_per_unit_applied ?? null,
    coverage_unit_applied: e.coverage_unit_applied ?? null,
    is_divisible_applied: e.is_divisible_applied ?? null,
    stock_length_applied: e.stock_length_applied ?? null,
    unit_key_applied: e.unit_key_applied ?? null,
  };
}

/**
 * Summary of the wastage that applies to a line (across its material
 * entries) for the estimate row badge. `pct` is null when entries differ.
 */
export function lineWastageSummary(line) {
  const entries = (line.recipe_snapshot && line.recipe_snapshot.recipe_materials) || [];
  if (!entries.length) return null;
  const resolved = entries.map((e) => resolveEntryWastage(e, line.wastage_pct_override));
  const pcts = new Set(resolved.map((r) => r.pct));
  const sources = new Set(resolved.map((r) => r.source));
  const source = sources.size === 1 ? resolved[0].source : "mixed";
  return {
    pct: pcts.size === 1 ? resolved[0].pct : null,
    source,
    label: source === "mixed" ? "varies by material" : WASTAGE_SOURCE_LABEL[source],
    entries: entries.map((e, i) => ({ description: e.description, ...resolved[i] })),
  };
}

/**
 * Set / clear the line-level wastage override and rebuild the material rate
 * from the snapshot arrays so the line cost follows (cell + snapshot baseline
 * move together — a wastage change is not a manual material_rate override).
 */
export function applyLineWastageOverride(line, pct) {
  const value = isBlank(pct) ? null : f(pct);
  const snap = line.recipe_snapshot;
  if (!snap) return { ...line, wastage_pct_override: value };
  const arrays = synthesiseSnapshotArrays(snap, line) || {};
  const entries = arrays.recipe_materials || [];
  const rate = entries.length ? r2(materialsRatePerUnit(entries, value)) : snap.material_rate;
  const next = { ...line, wastage_pct_override: value, recipe_snapshot: { ...snap, material_rate: rate } };
  if (!line.material_rate_override) next.material_rate = rate;
  return next;
}

export function computeRecipeTotals(item) {
  const labour = (item.recipe_labour || []).reduce((s, e) => s + f(e.hours_per_unit) * f(e.hourly_rate), 0);
  // LT-36 — a Flat Material Rate replaces the itemised figure outright.
  const flatOn = !!item.flat_material_enabled || !!(item.flat_material && item.flat_material.rate != null);
  const flatRate = item.flat_material_enabled ? f(item.flat_material_rate) : f(item.flat_material && item.flat_material.rate);
  const materials = flatOn ? flatRate : materialsRatePerUnit(item.recipe_materials, item.wastage_pct_override);
  const plant = (item.recipe_plant || []).reduce((s, e) => s + f(e.cost_per_unit), 0);
  const subby = (item.recipe_subby || []).reduce((s, e) => s + f(e.cost_per_unit), 0);
  const total = labour + materials + plant + subby;
  const hasRecipe = !!(
    (item.recipe_labour && item.recipe_labour.length) ||
    (!flatOn && item.recipe_materials && item.recipe_materials.length) ||
    (flatOn && flatRate > 0) ||
    (item.recipe_plant && item.recipe_plant.length) ||
    (item.recipe_subby && item.recipe_subby.length)
  );
  return {
    recipe_labour_total: r2(labour),
    recipe_materials_total: r2(materials),
    recipe_plant_total: r2(plant),
    recipe_subby_total: r2(subby),
    has_recipe: hasRecipe,
    flat_material: flatOn,
    current_rate: hasRecipe ? r2(total) : (item.current_rate ?? 0)
  };
}

export function buildSnapshot(libItem) {
  const labour = libItem.recipe_labour || [];
  const primary = labour[0] || null;
  const extras = labour.slice(1);
  // LT-36 — Flat Material Rate: the figure travels, the (ignored) entries don't.
  const flatOn = !!libItem.flat_material_enabled;
  const materialRate = flatOn ? f(libItem.flat_material_rate) : materialsRatePerUnit(libItem.recipe_materials);
  const plantRate    = (libItem.recipe_plant    || []).reduce((s, e) => s + f(e.cost_per_unit), 0);
  const subbyRate    = (libItem.recipe_subby    || []).reduce((s, e) => s + f(e.cost_per_unit), 0);
  const hasMat   = flatOn ? materialRate > 0 : !!(libItem.recipe_materials && libItem.recipe_materials.length);
  const hasPlant = !!(libItem.recipe_plant     && libItem.recipe_plant.length);
  const hasSubby = !!(libItem.recipe_subby     && libItem.recipe_subby.length);
  return {
    library_item_id: libItem.id,
    library_item_description: libItem.description,
    trade_name: primary ? primary.trade_name : null,
    hours_per_unit: primary ? r2(f(primary.hours_per_unit)) : null,
    hourly_rate: primary ? r2(f(primary.hourly_rate)) : null,
    material_rate: hasMat ? r2(materialRate) : null,
    plant_rate:    hasPlant ? r2(plantRate)  : null,
    subby_rate:    hasSubby ? r2(subbyRate)  : null,
    // LT-Estimate-UX-01 Section 3 — baseline description + unit captured at
    // insert. Only used for override detection (description is tracked but
    // never flags amber; unit does).
    description: libItem.description || null,
    unit: libItem.unit || null,
    additional_trades: extras.map((e) => ({
      id: e.id || newId(),
      trade_name: e.trade_name || "",
      hours_per_unit: r2(f(e.hours_per_unit)),
      hourly_rate: r2(f(e.hourly_rate))
    })),
    // Phase 9 — full itemised arrays so the per-tender drawer can edit recipe
    // entries independently from the master library item. Values are deep-copied
    // (with fresh ids) so mutating them on the line never bleeds back to the lib.
    recipe_labour: labour.map((e) => ({
      id: newId(),
      trade_name: e.trade_name || "",
      hours_per_unit: r2(f(e.hours_per_unit)),
      hourly_rate: r2(f(e.hourly_rate)),
      labour_rate_id: e.labour_rate_id || null
    })),
    recipe_materials: flatOn ? [] : (libItem.recipe_materials || []).map((e) => ({
      id: newId(),
      description: e.description || "",
      qty_per_unit: r2(f(e.qty_per_unit)),
      unit: e.unit || "",
      rate: r2(f(e.rate)),
      material_id: e.material_id || null,
      // LT-20 — quantity inputs travel with the snapshot; the server
      // re-resolves the *_applied fields from the library at insert.
      ...pickQuantityFields(e)
    })),
    flat_material: flatOn ? { rate: r2(materialRate), note: libItem.flat_material_note || null } : null,
    recipe_plant: (libItem.recipe_plant || []).map((e) => ({
      id: newId(),
      description: e.description || "",
      cost_per_unit: r2(f(e.cost_per_unit))
    })),
    recipe_subby: (libItem.recipe_subby || []).map((e) => ({
      id: newId(),
      description: e.description || "",
      cost_per_unit: r2(f(e.cost_per_unit))
    }))
  };
}

export function lineFromLibrary(libItem, qty = 1) {
  const snap = buildSnapshot(libItem);
  const q = lineQty(qty);
  // LT-Estimate-UX-01 Section 4 — labour mode. "estimated_each_use" items
  // arrive with hours=null (empty cell, focused on insert by TenderEstimate)
  // and the snapshot's hours_per_unit is dropped so override detection
  // never flags an "empty hours" as an override against a phantom baseline.
  const mode = libItem.labour_mode === "estimated_each_use" ? "estimated_each_use" : "rate_based";
  const modeSnap = mode === "estimated_each_use"
    ? {
        ...snap,
        hours_per_unit: null,
        additional_trades: (snap.additional_trades || []).map((t) => ({
          ...t,
          hours_per_unit: null,
        })),
      }
    : snap;
  const primaryHours = mode === "estimated_each_use"
    ? null
    : (snap.hours_per_unit != null ? r2(f(snap.hours_per_unit) * q) : null);
  const additionalTrades = (snap.additional_trades || []).map((t) => ({
    id: newId(),
    trade_name: t.trade_name,
    hours: mode === "estimated_each_use" ? null : r2(f(t.hours_per_unit) * q),
    hourly_rate: f(t.hourly_rate),
    hours_override: false,
    hourly_rate_override: false
  }));
  return {
    id: newId(),
    description: libItem.description || "",
    type: "library",
    library_item_id: libItem.id,
    trade_name: snap.trade_name || "",
    hours: primaryHours,
    hourly_rate: snap.hourly_rate,
    qty: q,
    unit: libItem.unit || "",
    material_rate: snap.material_rate,
    plant_rate: snap.plant_rate,
    subby_rate: snap.subby_rate,
    hours_override: false,
    hourly_rate_override: false,
    material_rate_override: false,
    plant_rate_override: false,
    subby_rate_override: false,
    unit_override: false,
    description_edited: false,
    additional_trades: additionalTrades,
    manual_override: false,
    notes: "",
    recipe_snapshot: modeSnap,
    labour_mode: mode,
    wastage_pct_override: null
  };
}

export function applyOverrideDetection(line) {
  if (!line.recipe_snapshot) return line;
  const snap = line.recipe_snapshot;
  const qty = lineQty(line.qty);
  const eqNum = (a, b) => Math.abs(f(a) - f(b)) < 0.01;
  const eqStr = (a, b) => String(a || "").trim() === String(b || "").trim();
  const out = { ...line };
  if (snap.hours_per_unit != null) {
    out.hours_override = !eqNum(line.hours, f(snap.hours_per_unit) * qty);
  }
  if (snap.hourly_rate != null)   out.hourly_rate_override   = !eqNum(line.hourly_rate,   snap.hourly_rate);
  if (snap.material_rate != null) out.material_rate_override = !eqNum(line.material_rate, snap.material_rate);
  if (snap.plant_rate != null)    out.plant_rate_override    = !eqNum(line.plant_rate,    snap.plant_rate);
  if (snap.subby_rate != null)    out.subby_rate_override    = !eqNum(line.subby_rate,    snap.subby_rate);

  // LT-Estimate-UX-01 Section 3 — unit diff (string). Legacy snapshots
  // without `unit` skip the diff silently.
  out.unit_override = !!snap.unit && !eqStr(line.unit, snap.unit);
  // LT-Estimate-UX-01 Section 3 — description tracking (not a pricing flag).
  out.description_edited = !!snap.description && !eqStr(line.description, snap.description);

  // LT-Estimate-UX-01 Section 3 — additional trades diff (index-aligned).
  // Extras added by estimator flag both hours + rate. Removed rows are
  // captured by the row-level manual_override via the length compare below.
  const snapExtras = snap.additional_trades || [];
  const lineExtras = line.additional_trades || [];
  const anyExtraRemoved = lineExtras.length < snapExtras.length;
  const newLineExtras = lineExtras.map((t, i) => {
    if (i < snapExtras.length) {
      const s = snapExtras[i];
      const expectedHours = f(s.hours_per_unit) * qty;
      const expectedRate = f(s.hourly_rate);
      return {
        ...t,
        hours_override: !eqNum(t.hours, expectedHours),
        hourly_rate_override: !eqNum(t.hourly_rate, expectedRate)
      };
    }
    return { ...t, hours_override: true, hourly_rate_override: true };
  });
  out.additional_trades = newLineExtras;

  // LT-Estimate-UX-01 Section 3 — derive row-level manual_override for
  // library lines. Description is intentionally EXCLUDED per user spec.
  const anyExtraOverride = anyExtraRemoved || newLineExtras.some(
    (t) => t.hours_override || t.hourly_rate_override
  );
  out.manual_override = !!(
    out.hours_override ||
    out.hourly_rate_override ||
    out.material_rate_override ||
    out.plant_rate_override ||
    out.subby_rate_override ||
    out.unit_override ||
    anyExtraOverride
  );
  return out;
}

export function reapplyRecipe(line) {
  if (!line.recipe_snapshot) return line;
  const snap = line.recipe_snapshot;
  const q = lineQty(line.qty);
  return {
    ...line,
    // LT-Estimate-UX-01 Section 3 — Reset to snapshot: description + unit
    // also revert. Legacy snapshots (no description/unit) leave those fields
    // as-is by falling back to the current line value.
    description: snap.description != null ? snap.description : (line.description || ""),
    unit: snap.unit != null ? snap.unit : (line.unit || ""),
    hours: snap.hours_per_unit != null ? r2(f(snap.hours_per_unit) * q) : null,
    hourly_rate: snap.hourly_rate ?? null,
    material_rate: snap.material_rate ?? null,
    plant_rate: snap.plant_rate ?? null,
    subby_rate: snap.subby_rate ?? null,
    hours_override: false,
    hourly_rate_override: false,
    material_rate_override: false,
    plant_rate_override: false,
    subby_rate_override: false,
    unit_override: false,
    description_edited: false,
    manual_override: false,
    additional_trades: (snap.additional_trades || []).map((t) => ({
      id: newId(),
      trade_name: t.trade_name,
      hours: r2(f(t.hours_per_unit) * q),
      hourly_rate: f(t.hourly_rate),
      hours_override: false,
      hourly_rate_override: false
    }))
  };
}

/**
 * Phase 9 — synthesise itemised recipe arrays from a snapshot's summary
 * fields. Used when opening the per-line recipe drawer for legacy lines whose
 * snapshot was written before the itemised arrays were captured. Produces a
 * minimal viable starting point: 1 labour row (primary), N labour rows for
 * additional_trades, 1 row each for non-zero materials / plant / subby rates.
 *
 *  - Returns the SAME snapshot if its arrays are already populated.
 *  - Pure (no side effects); safe to call repeatedly during render.
 */
export function synthesiseSnapshotArrays(snap, line) {
  if (!snap) return null;
  const has = (a) => Array.isArray(a) && a.length > 0;
  if (
    has(snap.recipe_labour) ||
    has(snap.recipe_materials) ||
    has(snap.recipe_plant) ||
    has(snap.recipe_subby) ||
    snap.flat_material            // LT-36 — flat rate IS the materials structure
  ) {
    // Already itemised — return as-is (defensive copy of arrays only).
    return {
      ...snap,
      recipe_labour:    [...(snap.recipe_labour || [])],
      recipe_materials: [...(snap.recipe_materials || [])],
      recipe_plant:     [...(snap.recipe_plant || [])],
      recipe_subby:     [...(snap.recipe_subby || [])]
    };
  }
  const lUnit = (line && line.unit) || "";
  const labour = [];
  if (snap.hours_per_unit || snap.hourly_rate) {
    labour.push({
      id: newId(),
      trade_name: snap.trade_name || (line && line.trade_name) || "",
      hours_per_unit: r2(f(snap.hours_per_unit)),
      hourly_rate: r2(f(snap.hourly_rate)),
      labour_rate_id: null
    });
  }
  for (const t of (snap.additional_trades || [])) {
    labour.push({
      id: newId(),
      trade_name: t.trade_name || "",
      hours_per_unit: r2(f(t.hours_per_unit)),
      hourly_rate: r2(f(t.hourly_rate)),
      labour_rate_id: null
    });
  }
  const materials = [];
  if (f(snap.material_rate) > 0) {
    materials.push({
      id: newId(),
      description: "Materials per unit",
      qty_per_unit: 1,
      unit: lUnit,
      rate: r2(f(snap.material_rate)),
      material_id: null
    });
  }
  const plant = [];
  if (f(snap.plant_rate) > 0) {
    plant.push({ id: newId(), description: "Plant per unit", cost_per_unit: r2(f(snap.plant_rate)) });
  }
  const subby = [];
  if (f(snap.subby_rate) > 0) {
    subby.push({ id: newId(), description: "Subby per unit", cost_per_unit: r2(f(snap.subby_rate)) });
  }
  return {
    ...snap,
    recipe_labour: labour,
    recipe_materials: materials,
    recipe_plant: plant,
    recipe_subby: subby
  };
}

/**
 * Compute summary per-unit rates from itemised recipe arrays.
 * Returns a partial snapshot object containing trade_name, hours_per_unit,
 * hourly_rate, material_rate, plant_rate, subby_rate, and additional_trades.
 */
export function summariseRecipeArrays(arrays, lineWastageOverride = null) {
  const labour = arrays.recipe_labour || [];
  const primary = labour[0] || null;
  const extras = labour.slice(1);
  // LT-36 — a Flat Material Rate on the snapshot stands in for itemised lines.
  const flat = arrays.flat_material && arrays.flat_material.rate != null ? arrays.flat_material : null;
  const materialRate = flat ? f(flat.rate) : materialsRatePerUnit(arrays.recipe_materials, lineWastageOverride);
  const plantRate    = (arrays.recipe_plant    || []).reduce((s, e) => s + f(e.cost_per_unit), 0);
  const subbyRate    = (arrays.recipe_subby    || []).reduce((s, e) => s + f(e.cost_per_unit), 0);
  const hasMat   = flat ? true : (arrays.recipe_materials || []).length > 0;
  const hasPlant = (arrays.recipe_plant     || []).length > 0;
  const hasSubby = (arrays.recipe_subby     || []).length > 0;
  return {
    trade_name: primary ? (primary.trade_name || null) : null,
    hours_per_unit: primary ? r2(f(primary.hours_per_unit)) : null,
    hourly_rate: primary ? r2(f(primary.hourly_rate)) : null,
    material_rate: hasMat ? r2(materialRate) : null,
    plant_rate:    hasPlant ? r2(plantRate)  : null,
    subby_rate:    hasSubby ? r2(subbyRate)  : null,
    additional_trades: extras.map((e) => ({
      id: e.id || newId(),
      trade_name: e.trade_name || "",
      hours_per_unit: r2(f(e.hours_per_unit)),
      hourly_rate: r2(f(e.hourly_rate))
    }))
  };
}

/**
 * Apply the snapshot recipe arrays to a line: rebuilds the line's cell values
 * (hours, hourly_rate, qty-driven labour, material_rate, plant_rate, subby_rate,
 * additional_trades) AND clears all override flags. Mirrors `reapplyRecipe`
 * but sources from the line's own snapshot arrays rather than the cached
 * summary fields — used after a per-tender drawer save.
 */
export function applyRecipeArraysToLine(line, arrays) {
  const summary = summariseRecipeArrays(arrays, line.wastage_pct_override);
  const q = lineQty(line.qty);
  const newSnap = {
    ...(line.recipe_snapshot || {}),
    ...summary,
    recipe_labour:    [...(arrays.recipe_labour || [])],
    recipe_materials: arrays.flat_material ? [] : [...(arrays.recipe_materials || [])],
    recipe_plant:     [...(arrays.recipe_plant || [])],
    recipe_subby:     [...(arrays.recipe_subby || [])],
    flat_material:    arrays.flat_material || null,   // LT-36
  };
  return {
    ...line,
    hours: summary.hours_per_unit != null ? r2(f(summary.hours_per_unit) * q) : null,
    hourly_rate: summary.hourly_rate,
    material_rate: summary.material_rate,
    plant_rate: summary.plant_rate,
    subby_rate: summary.subby_rate,
    trade_name: summary.trade_name || line.trade_name || "",
    hours_override: false,
    hourly_rate_override: false,
    material_rate_override: false,
    plant_rate_override: false,
    subby_rate_override: false,
    // LT-Estimate-UX-01 Section 3 — recipe drawer save also clears the new
    // flags. Row-level manual_override becomes false because there are no
    // longer any cell diffs.
    unit_override: false,
    description_edited: false,
    manual_override: false,
    additional_trades: (summary.additional_trades || []).map((t) => ({
      id: newId(),
      trade_name: t.trade_name,
      hours: r2(f(t.hours_per_unit) * q),
      hourly_rate: f(t.hourly_rate),
      hours_override: false,
      hourly_rate_override: false
    })),
    recipe_snapshot: newSnap
  };
}

/**
 * Per-unit cost computed from itemised arrays. Used by the inline read view
 * footer ("Per-unit total: £x.xx").
 */
export function recipePerUnitTotal(arrays) {
  const s = summariseRecipeArrays(arrays);
  return r2(
    f(s.hours_per_unit) * f(s.hourly_rate) +
    (arrays.recipe_labour || []).slice(1).reduce(
      (sum, e) => sum + f(e.hours_per_unit) * f(e.hourly_rate), 0
    ) +
    f(s.material_rate) +
    f(s.plant_rate) +
    f(s.subby_rate)
  );
}

/**
 * Derive a starting recipe (1-row per non-zero rate) from a tender line that
 * has no snapshot. Used by the Save-to-Library modal when promoting a fresh
 * ad-hoc line into a new master library item, per Q3 (Option A) of the
 * Polish prompt clarifications. The user can then add/remove/edit rows
 * freely inside the modal's RecipeBuilder before saving.
 *
 * Falls back to `synthesiseSnapshotArrays` when the line already has a
 * snapshot — including snapshots that already carry full itemised arrays.
 */
export function deriveArraysFromLine(line) {
  if (line && line.recipe_snapshot) {
    return synthesiseSnapshotArrays(line.recipe_snapshot, line) || {
      recipe_labour: [], recipe_materials: [], recipe_plant: [], recipe_subby: []
    };
  }
  if (!line) {
    return { recipe_labour: [], recipe_materials: [], recipe_plant: [], recipe_subby: [] };
  }
  const q = f(line.qty) || 1;
  const labour = [];
  if (f(line.hours) > 0 || f(line.hourly_rate) > 0 || (line.trade_name || "").trim()) {
    labour.push({
      id: newId(),
      trade_name: line.trade_name || "",
      hours_per_unit: r2(f(line.hours) / q),
      hourly_rate: r2(f(line.hourly_rate)),
      labour_rate_id: null
    });
  }
  for (const t of (line.additional_trades || [])) {
    labour.push({
      id: newId(),
      trade_name: t.trade_name || "",
      hours_per_unit: r2(f(t.hours) / q),
      hourly_rate: r2(f(t.hourly_rate)),
      labour_rate_id: null
    });
  }
  const materials = f(line.material_rate) > 0 ? [{
    id: newId(),
    description: "Materials per unit",
    qty_per_unit: 1,
    unit: line.unit || "",
    rate: r2(f(line.material_rate)),
    material_id: null
  }] : [];
  const plant = f(line.plant_rate) > 0 ? [{
    id: newId(),
    description: "Plant per unit",
    cost_per_unit: r2(f(line.plant_rate))
  }] : [];
  const subby = f(line.subby_rate) > 0 ? [{
    id: newId(),
    description: "Subby per unit",
    cost_per_unit: r2(f(line.subby_rate))
  }] : [];
  return {
    recipe_labour: labour,
    recipe_materials: materials,
    recipe_plant: plant,
    recipe_subby: subby
  };
}
